import type { IAddress, IAttribute, ILanguage, LogEntry, Responsible, Talent } from "@/api/talent/models";
import { BaseModel, PaginationResponse } from "@/interfaces/api";
import { UserDepartment } from "../user/models";
import type { PhotogenyRejectionReasonEnum } from "@/enums/photogeny";

export interface PhotogenyPayload {
  photographerId: string;
  evaluationDate: string;
  isApproved: boolean;
  failureReason: PhotogenyRejectionReasonEnum | null;
  observation: string | null;
}

export interface Photogeny {
  id: string;
  photographer: { id: string; name: string | null };
  evaluationDate: string;
  isApproved: boolean;
  failureReason: PhotogenyRejectionReasonEnum | null;
  observation: string | null;
}

export interface PhotographyResponse {
  photogeny: Photogeny | null;
}

export interface PhotographerOption {
  id: string;
  name: string | null;
}

type Nill<T> = { [P in keyof T]?: T[P] | undefined | null };

export type PhotoMaterialType = "B1" | "B2" | "B3" | "B4" | "BD";
export type AgencyingModality = "CASH" | "INSTALLMENT";
export type PhotoService = "PHOTO_MATERIAL" | "PHOTOGENY";

export interface RejectedCandidatesResponse extends PaginationResponse<RejectedCandidate> {
  filterOptions: {
    producers: { value: string; label: string }[];
    failureReasons: { value: string; label: string }[];
  };
}

export interface RejectedCandidate {
  id: string;
  talentId: string;
  fullName: string;
  birthday: string | null;
  profilePhotoUrl: string | null;
  talentCode: string | null;
  resultDate: string;
  responsibleProducer: string | null;
  producerId: string | null;
  failureReason: string;
  failureObservation: string | null;
  returnDate: string;
}

export interface SchedulePrefillResponsible {
  id: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  secondaryPhone: string | null;
}

export interface SchedulePrefill {
  id: string;
  talentCode: string;
  fullName: string;
  birthday: string;
  email: string | null;
  phone: string | null;
  secondaryPhone: string | null;
  responsibles?: SchedulePrefillResponsible[];
}

export interface GenerateTalentCodeResponse {
  talentCode: string;
}

export type ScheduleStage = "SCHEDULED" | "RECEPTION" | "PHOTOGRAPHY" | "PRODUCTION" | "ART_PRODUCTION";

export type ScheduleProductionLogSection = "AGENCYING" | "PAYMENT_METHODS" | "ARTISTIC_PRODUCTION";

export type SchedulePaymentMethod = "CREDIT_CARD" | "DEBIT_CARD" | "BANK_SLIP" | "CASH" | "TED_PIX";
export type SchedulePaymentMethodsState = "EMPTY" | "SAVED" | "NO_CHARGE" | "BELOW_MINIMUM";

export interface SchedulePaymentAttachment {
  token: string;
  name: string;
  mimeType: string;
  size: number;
  previewUrl: string;
  downloadUrl: string;
}

export interface SchedulePayment {
  id: string;
  paymentMethod: SchedulePaymentMethod;
  paidAmount: number;
  installmentsNumber: number | null;
  installmentValue: number | null;
  paymentDate: string;
  observation: string | null;
  attachments: SchedulePaymentAttachment[];
}

export interface SchedulePaymentMethods {
  state: SchedulePaymentMethodsState;
  revision: string;
  chargedAmountInCents: number | null;
  canEdit: boolean;
  reason: string | null;
  payments: SchedulePayment[];
}

export interface SavePaymentMethodsPayload {
  expectedRevision: string;
  payments: Array<{
    clientKey: string;
    id?: string;
    paymentMethod: SchedulePaymentMethod;
    paidAmount: number;
    installmentsNumber?: number;
    paymentDate?: string;
    observation?: string;
    retainedAttachmentTokens: string[];
    newFileKeys: string[];
  }>;
}

export interface SavePaymentMethodsResponse extends SchedulePaymentMethods {
  fileDeletionStatus: "COMPLETED" | "PENDING";
}

export interface TalentCandidateResult {
  id: string;
  isApproved: boolean;
  userId: string;
  userName: string | null;
  resultDate: string;
  photoMaterialType: PhotoMaterialType | null;
  modality: AgencyingModality | null;
  originalAmountInCents: number | null;
  chargedAmountInCents: number | null;
  failureReason: string | null;
  failureObservation: string | null;
  isReturnContact: boolean | null;
}

export interface TalentCandidateResultProducer {
  id: string;
  name: string | null;
  isAvailable: boolean;
}

export interface UpdateTalentCandidateResultPayload {
  resetPaymentMethods?: boolean;
  isApproved: boolean;
  userId: string;
  resultDate?: string;
  photoMaterialType?: PhotoMaterialType;
  modality?: AgencyingModality;
  chargedAmountInCents?: number;
  failureReason?: string;
  failureObservation?: string;
  isReturnContact?: boolean;
}

export type ScheduleProductionLog = {
  section: ScheduleProductionLogSection;
  action: string;
  authorName: string;
  createdDate: string;
};

export interface ChangeOfClothesResponsibleUser {
  id: string;
  name: string | null;
  isActive: boolean;
}

export interface UpdateChangeOfClothesItemPayload {
  id?: string;
  responsibleUserId: string;
  clothing: string;
  observation?: string;
}

export interface UpdateChangeOfClothesPayload {
  changes: UpdateChangeOfClothesItemPayload[];
}

export interface ChangeOfClothes {
  id: string;
  scheduleId: string;
  responsibleUser: ChangeOfClothesResponsibleUser;
  position: number;
  clothing: string | null;
  observation: string | null;
}

export interface UpdateChangeOfClothesResponseDto {
  changes: ChangeOfClothes[];
}

export const ScheduleStageEnum: Record<ScheduleStage, ScheduleStage> = {
  SCHEDULED: "SCHEDULED",
  RECEPTION: "RECEPTION",
  PHOTOGRAPHY: "PHOTOGRAPHY",
  PRODUCTION: "PRODUCTION",
  ART_PRODUCTION: "ART_PRODUCTION",
};

export enum ScheduleType {
  PHOTO_RENEWAL = "PHOTO_RENEWAL",
  NEW_CANDIDATE = "NEW_CANDIDATE",
}

export type ScheduleStageCounts = Record<ScheduleStage, number>;

export type ScheduleEntityType = "TALENT" | "TALENT_CANDIDATE";
export type ScheduleStatus = "CANCELED" | "COMPLETED" | "IN_PROGRESS" | "PAUSED" | "AWAITING";

export interface ScheduledParticipantResponsible {
  id?: string;
  fullName: string;
  email: string | null;
  phone: string | null;
  secondaryPhone: string | null;
}

export interface ScheduledCandidateParticipant {
  talentCode: string | null;
  origin: TalentCandidateOrigin;
  email: string | null;
  phone: string | null;
  secondaryPhone: string | null;
  responsibles: ScheduledParticipantResponsible[];
}

export interface ScheduledTalentParticipant {
  talentCode: string | null;
  email: string | null;
  phone: string | null;
  secondaryPhone: string | null;
  responsibles: ScheduledParticipantResponsible[];
}

export interface ScheduledParticipant {
  fullName: string;
  birthday?: string;
  age: string | null;
  picture: { url: string };
  candidate?: ScheduledCandidateParticipant;
  talent?: ScheduledTalentParticipant;
}

export interface ScheduleTalentDataResponsible
  extends Pick<
    Responsible,
    | "id"
    | "fullName"
    | "cpf"
    | "rg"
    | "kinship"
    | "email"
    | "phone"
    | "secondaryPhone"
    | "birthday"
    | "address"
    | "associatedTalents"
  > {}

export type ScheduleTalentDataResponsibleReference = Pick<ScheduleTalentDataResponsible, "id" | "fullName">;

export interface ScheduleTalentDataAttribute
  extends Pick<
    IAttribute,
    | "id"
    | "height"
    | "shoeSize"
    | "clothingSize"
    | "eyeColor"
    | "naturalHairColor"
    | "currentHairColor"
    | "naturalHairType"
    | "currentHairType"
    | "skinColor"
    | "physicalFeatures"
    | "hasTattoo"
    | "languages"
    | "attributeSkills"
  > {}

export type ScheduleTalentData = Nill<Talent> &
  Pick<Talent, "fullName" | "birthday" | "email" | "phone" | "secondaryPhone"> & {
    logs?: LogEntry[];
    origin: TalentCandidateOrigin | null;
    responsible: ScheduleTalentDataResponsibleReference | null;
    responsibles?: ScheduleTalentDataResponsible[];
    scheduleDate: string;
    attribute?: ScheduleTalentDataAttribute | null;
  };

export interface ScheduleDetail extends BaseModel {
  status: ScheduleStatus;
  currentStage: ScheduleStage;
  isPresent: boolean;
  scheduleType: ScheduleType;
  department: UserDepartment.REBOUCAS | UserDepartment.FRADIQUE;
  photoService: PhotoService | null;
  photoMaterialType?: PhotoMaterialType | null;
  scheduleDate?: string;
  pauseReason?: SchedulePauseReason | null;
  cancellationReason?: ScheduleCancellationReason | null;
  cancellationReasonDetails?: string | null;
  responsibleProducerId?: string | null;
  pausedByUser?: { id: string; name: string | null } | null;
  canceledByUser?: { id: string; name: string | null } | null;
  participant: ScheduledParticipant;
}

export interface ScheduleSuggestion {
  scheduleId: string;
  fullName: string;
  talentCode: string | null;
  responsibleName: string | null;
  profilePhotoUrl: string | null;
  scheduleDate: string;
  currentStage: ScheduleStage;
}

export interface ScheduledSchedule {
  scheduleId: string;
  talentId: string;
  isPresent: boolean;
  fullName: string;
  talentCode: string | null;
  responsibleName: string | null;
  scheduleType: ScheduleType;
  scheduleDate: string;
  currentStage: ScheduleStage;
  lastStage: ScheduleStage | null;
  lastStageTime: string | null;
  arrivalDate: string | null;
  hasPriority: boolean;
  currentStatus: ScheduleStatus;
  photoService: PhotoService | null;
}

export interface CreateScheduledResponsiblePayload {
  fullName: string;
  email: string;
  phone: string;
  secondaryPhone?: string;
}

export type TalentCandidateOrigin = "SPONTANEOUS_ENTRY" | "SCOUTER" | "WEB_SCOUTER" | "SITE_OR_LANDING_PAGE";

export interface CreateCandidateForSchedulePayload {
  fullName: string;
  birthday: Date;
  origin: TalentCandidateOrigin;
  email?: string;
  phone?: string;
  secondaryPhone?: string;
  responsible?: CreateScheduledResponsiblePayload;
}

export interface CreatePhotoRenewalSchedulePayload {
  talentId: string;
  responsibleId?: string;
  photoMaterialType: PhotoMaterialType;
  photoService?: PhotoService;
  scheduleDate: Date;
}

export interface CreateNewCandidateSchedulePayload {
  scheduleDate: Date;
  candidate: CreateCandidateForSchedulePayload;
}

export interface UpdateSchedulePresencePayload {
  isPresent: boolean;
}

export interface RescheduleCandidateContactPayload {
  email?: string | null;
  phone?: string | null;
  secondaryPhone?: string | null;
}

export interface ReschedulePhotoRenewalPayload {
  responsibleId?: string;
}

export interface RescheduleSchedulePayload {
  scheduleDate: string;
  scheduleTime: string;
  candidate?: RescheduleCandidateContactPayload & {
    responsible?: RescheduleCandidateContactPayload;
  };
  photoRenewal?: ReschedulePhotoRenewalPayload;
}

export interface PreferredProducer {
  id: string;
  name: string | null;
  isAvailable: boolean;
}

export enum SchedulePauseReason {
  TALENT_UNWELL_OR_SLEEPING = "TALENT_UNWELL_OR_SLEEPING",
  TALENT_AGITATED = "TALENT_AGITATED",
  TALENT_OR_RESPONSIBLE_HAS_COMMITMENT = "TALENT_OR_RESPONSIBLE_HAS_COMMITMENT",
  TALENT_MEAL_BREAK = "TALENT_MEAL_BREAK",
}

export enum ScheduleCancellationReason {
  REGRET = "REGRET",
  FINANCIAL_ISSUES = "FINANCIAL_ISSUES",
  THIRD_PARTY_INFLUENCE = "THIRD_PARTY_INFLUENCE",
  RESPONSIBLE_DISAGREEMENT = "RESPONSIBLE_DISAGREEMENT",
  TALENT_WITHDRAWAL = "TALENT_WITHDRAWAL",
  PHOTO_DELAY = "PHOTO_DELAY",
  MATERIAL_DELIVERY_DELAY = "MATERIAL_DELIVERY_DELAY",
}

export interface PauseSchedulePayload {
  reason: SchedulePauseReason;
}

export interface CancelSchedulePayload {
  reason: ScheduleCancellationReason;
  reasonDetails?: string;
}

export interface UpdateScheduleStagePayload {
  stage: ScheduleStage;
  photoService?: PhotoService;
  responsibleProducerId?: string;
}

export type ScheduleTalentDataSection =
  | "personal-information"
  | "photogeny"
  | "agency"
  | "material-photography"
  | "characteristics"
  | "measurements"
  | "abilities"
  | "responsibles";

export type ResponsibleUpdatePayload = Omit<
  Responsible,
  "createdDate" | "updatedDate" | "address" | "associatedTalents"
> & {
  id?: string;
  address?: Omit<IAddress, "id">;
};

export type UpdateScheduleTalentDataPayload = Partial<
  Pick<
    ScheduleTalentData,
    | "fullName"
    | "artisticName"
    | "birthday"
    | "email"
    | "rg"
    | "cpf"
    | "genderIdentity"
    | "nationality"
    | "hasBiologicalSibling"
    | "isTwinSibling"
    | "hasDisability"
    | "disabilityType"
    | "hasPhotoMaterial"
    | "isRepresentedByAnotherAgency"
    | "hasPreviousWorkExperience"
    | "hasDRT"
    | "address"
    | "phone"
    | "secondaryPhone"
    | "socialMedias"
  >
> & {
  responsibles?: ResponsibleUpdatePayload[];
  attribute?: Nill<
    Pick<
      ScheduleTalentDataAttribute,
      | "eyeColor"
      | "naturalHairColor"
      | "currentHairColor"
      | "naturalHairType"
      | "currentHairType"
      | "skinColor"
      | "physicalFeatures"
      | "hasTattoo"
    >
  > & {
    languages?: ILanguage[];
    attributeSkills?: { skill: string; category?: string; details?: string }[];
    attributeSkillsToDelete?: string[];
  } & Nill<Pick<ScheduleTalentDataAttribute, "height" | "shoeSize" | "clothingSize">>;
};

export interface MaterialPhotographyExchange {
  id: string;
  photographerId: string;
  photographerName: string;
  observation?: string;
  photos: readonly { id: string; url: string; alt?: string }[];
}

export type PhotographyData = {
  contractedMaterial: string | null;
  producerName: string | null;
  materialPhotographyExchanges: readonly MaterialPhotographyExchange[];
};
